/**
 * ConfigurationPanel Component
 * 
 * Handles Question Type, Question Style, and Difficulty Level selection
 * Uses Radio Groups for single-click selection where appropriate
 */

import React from "react";
import { 
  Radio, 
  Select, 
  Space, 
  Typography,
  Spin 
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { ConfigurationPanelProps } from "../types";
import { 
  QUESTION_TYPES, 
  DIFFICULTY_LEVELS, 
  SHARED_STYLES 
} from "../constants";

const { Text } = Typography;

const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
  selectedQuestionType,
  onQuestionTypeChange,
  filteredQuestionStyles,
  selectedQuestionStyle,
  onQuestionStyleChange,
  selectedDifficulty,
  onDifficultyChange,
  isLoadingStyles,
  disabled = false,
}) => {
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {/* Question Type - Radio Group */}
      <div>
        <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: SHARED_STYLES.fontSizeSm }}>
          Question Type
        </Text>
        <Radio.Group
          value={selectedQuestionType}
          onChange={(e) => onQuestionTypeChange(e.target.value)}
          buttonStyle="solid"
          optionType="button"
          disabled={disabled}
          style={{ width: "100%" }}
        >
          <Space wrap style={{ width: "100%" }}>
            {QUESTION_TYPES.map((type) => (
              <Radio.Button 
                key={type.value} 
                value={type.value}
                style={{ flex: "1 1 45%", textAlign: "center" }}
              >
                {type.label}
              </Radio.Button>
            ))}
          </Space>
        </Radio.Group>
      </div>

      {/* Question Style - Select Dropdown with descriptions */}
      <div>
        <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: SHARED_STYLES.fontSizeSm }}>
          Question Style
        </Text>
        {isLoadingStyles ? (
          <div style={{ padding: "16px", textAlign: "center" }}>
            <Spin indicator={<LoadingOutlined spin />} size="small" />
            <Text type="secondary" style={{ marginLeft: "8px" }}>
              Loading...
            </Text>
          </div>
        ) : (
          <Select
            placeholder="Select a question style"
            value={selectedQuestionStyle || undefined}
            onChange={onQuestionStyleChange}
            disabled={disabled || !selectedQuestionType}
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            optionLabelProp="label"
            filterOption={(input, option) =>
              String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={
              !selectedQuestionType 
                ? "Select Question Type first" 
                : "No styles available"
            }
          >
            {filteredQuestionStyles.map((style) => (
              <Select.Option 
                key={style.value} 
                value={style.value}
                label={style.label}
              >
                <div>
                  <Text>{style.label}</Text>
                  {style.description && (
                    <Text 
                      type="secondary" 
                      style={{ 
                        display: "block", 
                        fontSize: SHARED_STYLES.fontSizeSm,
                        lineHeight: 1.4,
                      }}
                    >
                      {style.description}
                    </Text>
                  )}
                </div>
              </Select.Option>
            ))}
          </Select>
        )}
      </div>

      {/* Difficulty Level - Radio Group */}
      <div>
        <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: SHARED_STYLES.fontSizeSm }}>
          Difficulty Level
        </Text>
        <Radio.Group
          value={selectedDifficulty}
          onChange={(e) => onDifficultyChange(e.target.value)}
          buttonStyle="solid"
          optionType="button"
          disabled={disabled}
          style={{ width: "100%" }}
        >
          <Space wrap style={{ width: "100%" }}>
            {DIFFICULTY_LEVELS.map((level) => (
              <Radio.Button 
                key={level.value} 
                value={level.value}
                style={{ flex: "1 1 45%", textAlign: "center" }}
              >
                {level.label}
              </Radio.Button>
            ))}
          </Space>
        </Radio.Group>
      </div>
    </Space>
  );
};

export default ConfigurationPanel;

